/**
 * @file QueryList
 * @author you.zhang
 */
import cn from 'classnames'
import { debounce, get, isEmpty, pickBy, throttle } from 'lodash'
import React, { createRef } from 'react'
import { findDOMNode } from 'react-dom'

import { getLanguage, getText } from '../prev-locale'
import Spin from '../spin'
import Table from '../table'

export default class QueryList extends React.PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      loading: false,
      dataSource: [],
      pagination: {
        // pageSize: 20,
        current: 1,
        total: 0,
      },
      scroll: props.scroll,
      hasScrollToBottom: !props.scroll?.y,
    }

    this.tableRef = createRef()

    const { actions } = props
    actions.on('search', this.onSearch)
    actions.on('setTableDataSource', this.updateDataSource)
    actions.on('setPagination', this.updatePagination)
    actions.on('setFormData', this.onFormChange)
  }

  render() {
    const {
      columns,
      children,
      pagination,
      localPagination = false,
      paginationSticky = false,
      className = '',
      qlsProps,
      ...rest
    } = this.props
    const { dataSource, loading, expandedRowKeys = [], scroll, hasScrollToBottom } = this.state
    const hasPagination = typeof pagination !== 'undefined' ? pagination : true
    const paginationInfo = {
      ...this.state.pagination,
      // size: 'small',
      showQuickJumper: true,
      showSizeChanger: true,
      showTotal: (total) => getText('totalRecords', getLanguage(), total),
      ...(pagination || {}),
    }
    const expandProps = {
      ...(rest.defaultExpandAllRows
        ? {
            expandedRowKeys,
            onExpandedRowsChange: this.onExpandedRowsChange,
          }
        : {}),
    }

    return (
      <div
        className={cn(`tnt-querylistscene-list ${className}`, {
          paginationSticky,
          hasScrollToBottom,
        })}
      >
        <Spin spinning={loading}>
          <Table
            ref={this.tableRef}
            shadowed={false}
            bordered={false}
            {...expandProps}
            {...rest}
            {...(columns
              ? {
                  columns,
                }
              : {})}
            scroll={scroll}
            dataSource={dataSource}
            pagination={hasPagination ? paginationInfo : localPagination}
            onChange={this.onTableChange}
          >
            {!columns && children}
          </Table>
        </Spin>
      </div>
    )
  }

  onWindowResize = debounce(() => {
    const { scroll } = this.state

    this.setState({
      scroll: {
        ...scroll,
        y: window.innerHeight - (this.initialTop || this.props.top || 200),
      },
    })
  }, 50)

  componentDidMount() {
    const { qlsProps, actions } = this.props

    this.formData = {
      ...(actions.getFormData() || {}),
      // 记住上次查询的分页参数
      ...(qlsProps.memory ? actions.getPagination() : {}),
    }

    if (!qlsProps.initSearch) {
      // 默认进来不请求
      this.fetchData(this.formData).finally(() => {
        // 轮询
        if (qlsProps.interval && !this.isUnmounted) {
          const doIntervalQuery = (ms) => {
            // 组件unmounted后，不执行轮训逻辑
            if (!this.isUnmounted) {
              this.timmer = setTimeout(() => {
                // 如果有查询请求还未结束，则该次轮询不执行，重新设置下次轮询
                if (!this.isFetching) {
                  this.fetchData(actions.getSubmittedFormData(), {
                    isInterval: true,
                    showLoading: false,
                  }).then(() => {
                    doIntervalQuery(ms)
                  })
                } else {
                  doIntervalQuery(ms)
                }
              }, ms)
            }
          }
          doIntervalQuery(qlsProps.interval)
        }
      })
    }
    if (this.state.scroll?.y) {
      window.addEventListener('resize', this.onWindowResize)

      if (this.tableRef.current) {
        // table内垂直滚动，需要动态设置分页组件上阴影样式
        const tableBody = findDOMNode(this.tableRef.current).querySelector('.ant-table-body')
        this.initialTop = window.innerHeight - this.state.scroll.y
        this.updateIsScrollToBottom(tableBody)
        tableBody.addEventListener('scroll', this.onTableBodyScroll)
      }
    }
  }

  componentWillUnmount() {
    const { actions } = this.props

    this.isUnmounted = true

    actions.removeListener('search', this.onSearch)
    actions.removeListener('setTableDataSource', this.updateDataSource)
    actions.removeListener('setPagination', this.updatePagination)
    actions.removeListener('setFormData', this.onFormChange)
    window.removeEventListener('resize', this.onWindowResize)
    this.timmer && clearTimeout(this.timmer)
  }

  fetchData = (params = {}, { callback, showLoading = true, isInterval } = {}) => {
    const {
      qlsProps: { query },
      defaultExpandAllRows,
      rowKey,
      actions,
    } = this.props
    const { pagination } = this.state
    const hasPagination = this.props.pagination !== false

    if (hasPagination) {
      params = {
        pageSize: pagination.pageSize,
        current: pagination.current,
        ...params,
      }
    }

    showLoading &&
      this.setState({
        loading: true,
      })

    // 当用户操作查询还未得到服务端响应前,不要执行轮询请求时序问题导致覆盖
    if (!isInterval) {
      this.isFetching = true
    }

    actions.setData('submittedFormData', params)

    return query(pickBy(params, (val) => val !== '' && val !== null && val !== undefined))
      .then((result) => {
        const dataSource = hasPagination ? result.data || [] : result

        this.setState(
          {
            loading: false,
            dataSource,
            pagination: hasPagination
              ? {
                  pageSize: result.pageSize || pagination.pageSize || 10,
                  current: result.current || params.current || pagination.current || 1,
                  total: result.total || 0,
                }
              : null,
            ...(defaultExpandAllRows
              ? {
                  expandedRowKeys: dataSource.map((row) => row[rowKey]),
                }
              : {}),
          },
          () => {
            actions.setData('pagination', this.state.pagination)
            actions.setData('dataSource', this.state.dataSource)
          }
        )
      })
      .finally(() => {
        this.isFetching = false
        showLoading &&
          this.setState({
            loading: false,
          })
        callback && callback()
      })
  }

  updateDataSource = (dataSource) => {
    this.setState({
      dataSource,
    })
    this.props.actions.setData('dataSource', dataSource)
  }

  updatePagination = (pagination) => {
    this.setState({
      pagination,
    })
    this.props.actions.setData('pagination', pagination)
  }

  onFormChange = (values) => {
    this.formData = pickBy(
      {
        ...values,
        sorter: get(this.formData, 'sorter'),
        filters: get(this.formData, 'filters'),
      },
      (val) => val !== undefined
    )
  }

  onSearch = (values, { callback, showLoading = true } = {}) => {
    this.formData = {
      ...this.formData,
      ...values,
    }
    // this.props.actions.setData('formData', this.formData);
    return this.fetchData(this.formData, { callback, showLoading })
  }

  onTableChange = (pagination, filters, sorter) => {
    const { actions, localPagination } = this.props
    const tableParams = {
      pageSize: pagination.pageSize,
      current: pagination.current,
      filters: isEmpty(filters) ? undefined : filters,
      sorter: isEmpty(sorter) ? undefined : sorter,
    }

    Object.assign(this.formData, tableParams)

    // 排序变化暂时不处理，因为有时可能是前端sorter不需要走请求
    // 所以暂时排序有使用者自己处理
    if (!localPagination) {
      this.fetchData({
        ...actions.getSubmittedFormData(),
        ...tableParams,
      })
      return
    }

    const { dataSource } = this.state

    // 前端本地分页
    if (localPagination) {
      this.setState(
        {
          dataSource,
          pagination: {
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
          },
        },
        () => {
          actions.setData('formData', this.formData)
          actions.setData('pagination', this.state.pagination)
        }
      )
    }
  }

  onTableBodyScroll = throttle((evt) => {
    this.updateIsScrollToBottom(evt.target)
  }, 50)

  updateIsScrollToBottom = (tableBody) => {
    const isScrollToBottom = (tableBody) => {
      const { scrollHeight, scrollTop, offsetHeight } = tableBody

      return scrollHeight === scrollTop + offsetHeight
    }

    this.setState({
      hasScrollToBottom: isScrollToBottom(tableBody),
    })
  }
}
